import React, { useState, useEffect } from 'react';
import { interactiveLearningService } from '../../services/interactiveLearningService';
import { runPlaygroundCode } from '../../utils/playgroundRunner';
import './CodingContentViewer.css';

const LANGUAGE_ICONS = {
  python: '🐍',
  javascript: '🟨',
  java: '☕',
  cpp: '⚙️',
  html: '🌐',
  css: '🎨',
  sql: '🗄️',
  other: '💻'
};

const CodingContentViewer = ({ courseId, topicName, userId }) => {
  const [codingExamples, setCodingExamples] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedExampleIndex, setSelectedExampleIndex] = useState(0);
  const [editableCode, setEditableCode] = useState('');
  const [output, setOutput] = useState('');
  const [isRunning, setIsRunning] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [lastRunLanguage, setLastRunLanguage] = useState(null);

  useEffect(() => {
    const loadContent = async () => {
      if (!courseId || !topicName) {
        setLoading(false);
        return;
      }

      try {
        const content = await interactiveLearningService.getTopicContent(courseId, topicName);
        // Support both old format (single coding) and new format (array)
        if (content?.codingExamples && Array.isArray(content.codingExamples)) {
          const enabledExamples = content.codingExamples.filter(ex => ex.enabled !== false);
          setCodingExamples(enabledExamples);
          if (enabledExamples.length > 0) {
            setEditableCode(enabledExamples[0].code || '');
          }
        } else if (content?.coding && content.coding.enabled !== false) {
          // Old format: convert to array
          setCodingExamples([content.coding]);
          setEditableCode(content.coding.code || '');
        } else {
          setCodingExamples([]);
        }
      } catch (error) {
        console.debug('No coding content found for this topic');
        setCodingExamples([]);
      } finally {
        setLoading(false);
      }
    };

    loadContent();
  }, [courseId, topicName]);

  const currentExample = codingExamples[selectedExampleIndex];

  useEffect(() => {
    if (currentExample) {
      setEditableCode(currentExample.code || '');
      setOutput('');
      setLastRunLanguage(null);
      setIsEditing(false);
    }
  }, [selectedExampleIndex, codingExamples]);

  const handleRunCode = async () => {
    if (!editableCode.trim()) {
      setOutput('No code to run.');
      return;
    }

    setIsRunning(true);
    setOutput('Loading Python runtime...');

    try {
      // Use the language from current example
      const language = currentExample?.language || null;
      const result = await runPlaygroundCode(editableCode, language);
      setLastRunLanguage(result.language);
      setOutput(result.output || 'Code executed (no output).');
    } catch (error) {
      setOutput(`Error: ${error.message}`);
      setLastRunLanguage(null);
    } finally {
      setIsRunning(false);
    }
  };

  const handleResetCode = () => {
    if (currentExample?.code) {
      setEditableCode(currentExample.code);
      setOutput('');
      setLastRunLanguage(null);
      setIsEditing(false);
    }
  };

  if (loading) {
    return (
      <div className="coding-content-loading">
        <p>Loading coding content...</p>
      </div>
    );
  }

  if (codingExamples.length === 0) {
    return (
      <div className="coding-content-empty">
        <div className="empty-icon">💻</div>
        <h3>No Coding Content Yet</h3>
        <p>Your instructor hasn't added coding content for this topic yet.</p>
      </div>
    );
  }

  if (!currentExample) {
    return null;
  }

  const languageIcon = LANGUAGE_ICONS[currentExample.language] || '💻';
  const languageLabel = currentExample.language.charAt(0).toUpperCase() + currentExample.language.slice(1);

  return (
    <div className="coding-content-viewer">
      {/* Example Selector */}
      {codingExamples.length > 1 && (
        <div className="example-selector">
          <label>Select Example:</label>
          <select
            value={selectedExampleIndex}
            onChange={(e) => setSelectedExampleIndex(parseInt(e.target.value))}
            className="example-select"
          >
            {codingExamples.map((ex, idx) => (
              <option key={idx} value={idx}>
                {ex.title || `Example ${idx + 1}`}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="coding-header">
        <div className="coding-language-badge">
          <span className="language-icon">{languageIcon}</span>
          <span className="language-name">{languageLabel}</span>
        </div>
        {currentExample.title && (
          <h3 className="example-title">{currentExample.title}</h3>
        )}
      </div>

      {currentExample.description && (
        <div className="coding-description">
          <h4>📝 About This Code</h4>
          <p>{currentExample.description}</p>
        </div>
      )}

      <div className="coding-display">
        <div className="code-header">
          <span className="code-label">Code Example</span>
          <div className="code-actions">
            <button 
              className="edit-code-btn"
              onClick={() => setIsEditing(!isEditing)}
            >
              {isEditing ? '👁️ View Only' : '✏️ Edit Code'}
            </button>
            {isEditing && (
              <button 
                className="reset-code-btn"
                onClick={handleResetCode}
              >
                🔄 Reset
              </button>
            )}
            <button 
              className="run-code-btn"
              onClick={handleRunCode}
              disabled={isRunning || !editableCode.trim()}
            >
              {isRunning ? '⏳ Running...' : '▶️ Run Code'}
            </button>
            <button 
              className="copy-code-btn"
              onClick={() => {
                navigator.clipboard.writeText(editableCode);
                alert('Code copied to clipboard!');
              }}
            >
              📋 Copy
            </button>
          </div>
        </div>
        
        {isEditing ? (
          <div className="code-editor-container">
            <textarea
              value={editableCode}
              onChange={(e) => setEditableCode(e.target.value)}
              className="code-editor"
              spellCheck="false"
              placeholder="Enter your code here..."
            />
          </div>
        ) : (
          <div className="code-container">
            <pre><code>{editableCode}</code></pre>
          </div>
        )}

        {output && (
          <div className="code-output">
            <div className="output-header">
              <div className="output-header-left">
                <span className="output-label">Output</span>
                {lastRunLanguage && (
                  <span className="runtime-badge">
                    Runtime: {lastRunLanguage === 'javascript' ? '🟨 JavaScript' : 
                              lastRunLanguage === 'python' ? '🐍 Python' : 
                              lastRunLanguage}
                  </span>
                )}
              </div>
              <button 
                className="clear-output-btn"
                onClick={() => {
                  setOutput('');
                  setLastRunLanguage(null);
                }}
              >
                ✕ Clear
              </button>
            </div>
            <div className="output-content">
              <pre>{output}</pre>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CodingContentViewer;

